<template>
  <LayoutMain>
    <template #header>
      <PageHeader :icon="Brain" :title="t('duonamo.title')" :subtitle="t('duonamo.subtitle')" />
    </template>

    <!-- Game not started / Menu -->
    <div v-if="!isGameActive && !currentSession" class="space-y-6">
      <!-- Player Progress Card -->
      <Card class="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-2xl font-bold text-blue-900">
              {{ t('duonamo.level') }} {{ currentLevel }}
            </h2>
            <p class="text-blue-700">{{ totalXP }} XP</p>
          </div>
          <div class="text-right">
            <div class="text-sm text-blue-600 mb-1">
              {{ t('duonamo.progressTo') }} {{ currentLevel + 1 }}
            </div>
            <div class="w-32 h-2 bg-blue-200 rounded-full overflow-hidden">
              <div
                class="h-full bg-blue-500 transition-all duration-300"
                :style="{ width: ((totalXP % 100) / 100) * 100 + '%' }"
              />
            </div>
            <div class="text-xs text-blue-600 mt-1">
              {{ 100 - (totalXP % 100) }} {{ t('duonamo.xpToGo') }}
            </div>
          </div>
        </div>

        <!-- Enhanced Stats Quick View -->
        <div v-if="userStats" class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div class="text-lg font-bold text-blue-900">
              {{ userStats.totalSessions }}
            </div>
            <div class="text-xs text-blue-600">
              {{ t('duonamo.sessions') }}
            </div>
          </div>
          <div>
            <div class="text-lg font-bold text-blue-900">{{ userStats.averageAccuracy }}%</div>
            <div class="text-xs text-blue-600">
              {{ t('duonamo.accuracy') }}
            </div>
          </div>
          <div>
            <div class="text-lg font-bold text-blue-900">
              {{ userStats.currentStreak }}
            </div>
            <div class="text-xs text-blue-600">
              {{ t('duonamo.streak') }}
            </div>
          </div>
          <div>
            <div class="text-lg font-bold text-blue-900">
              {{ userStats.peopleProgress?.mastered || 0 }}
            </div>
            <div class="text-xs text-blue-600">
              {{ t('duonamo.mastered') }}
            </div>
          </div>
        </div>
      </Card>

      <!-- Learning Progress Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <h3 class="font-semibold mb-3 text-primary">
            {{ t('duonamo.learningProgress') }}
          </h3>
          <div class="space-y-3">
            <!-- Total People Available -->
            <div class="flex justify-between items-center">
              <span class="text-sm">{{ t('duonamo.totalPeople') }}</span>
              <span class="font-medium text-lg">{{ peopleProgressTotal }}</span>
            </div>

            <!-- Progress Breakdown -->
            <div v-if="userStats?.peopleProgress" class="space-y-2">
              <div class="flex justify-between items-center">
                <span class="text-sm text-green-600">{{ t('duonamo.mastered') }}:</span>
                <span class="font-medium text-green-600">{{
                  userStats.peopleProgress.mastered
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-blue-600">{{ t('duonamo.practiced') }}:</span>
                <span class="font-medium text-blue-600">{{
                  userStats.peopleProgress.practiced
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-orange-600">{{ t('duonamo.learning') }}:</span>
                <span class="font-medium text-orange-600">{{
                  userStats.peopleProgress.learning
                }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600">{{ t('duonamo.notStarted') }}:</span>
                <span class="font-medium text-gray-600">{{ notStartedPeople }}</span>
              </div>
            </div>

            <!-- Progress Bar -->
            <div v-if="userStats?.peopleProgress" class="mt-4">
              <div class="text-xs text-gray-600 mb-1">
                {{ t('duonamo.overallProgress') }}
              </div>
              <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div class="h-full flex">
                  <div
                    class="bg-green-500"
                    :style="{
                      width:
                        (userStats.peopleProgress.mastered / peopleProgressDenominator) * 100 + '%'
                    }"
                  />
                  <div
                    class="bg-blue-500"
                    :style="{
                      width:
                        (userStats.peopleProgress.practiced / peopleProgressDenominator) * 100 + '%'
                    }"
                  />
                  <div
                    class="bg-orange-500"
                    :style="{
                      width:
                        (userStats.peopleProgress.learning / peopleProgressDenominator) * 100 + '%'
                    }"
                  />
                </div>
              </div>
              <div class="text-xs text-gray-600 mt-1">
                {{ encounteredPercent }}% {{ t('duonamo.peopleEncountered') }}
              </div>
            </div>

            <div class="flex justify-between text-sm text-gray-500 pt-2 border-t">
              <span>{{ t('duonamo.lastPlayed') }}</span>
              <span>{{ formatLastPlayed(userStats?.lastCompletedAt) }}</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 class="font-semibold mb-3 text-primary">
            {{ t('duonamo.howItWorks') }}
          </h3>
          <ul class="text-sm space-y-2 text-gray-600">
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">•</span>
              <span>{{ t('duonamo.learnNames') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">•</span>
              <span>{{ t('duonamo.completeVariousChallenges') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">•</span>
              <span>{{ t('duonamo.buildConfidence') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-blue-500 mt-1">•</span>
              <span>{{ t('duonamo.earnXP') }}</span>
            </li>
          </ul>
        </Card>
      </div>

      <!-- Action Buttons -->
      <div class="space-y-3">
        <Button
          :disabled="loading || !allPeople || allPeople.length < 3"
          variant="primary"
          size="lg"
          class="w-full"
          @click="startGame"
        >
          <Loader2 v-if="loading" class="h-5 w-5 mr-2 animate-spin" />
          <Play v-else class="h-5 w-5 mr-2" />
          {{ getStartButtonText() }}
        </Button>

        <div class="flex gap-3">
          <Button variant="outline" class="flex-1" @click="showStats = true">
            <BarChart3 class="h-4 w-4 mr-2" />
            {{ t('duonamo.viewStats') }}
          </Button>
          <Button variant="outline" class="flex-1" @click="showLeaderboard = true">
            <Trophy class="h-4 w-4 mr-2" />
            {{ t('duonamo.leaderboard') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Active Game -->
    <div v-if="isGameActive && currentChallenge" class="space-y-4">
      <!-- Session Progress -->
      <Card class="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <div class="space-y-4">
          <div class="flex items-center justify-between gap-4">
            <div>
              <h3 class="font-semibold text-green-900">
                {{ getSessionTypeDisplay(currentSession?.session_type) }}
              </h3>
              <p class="text-sm text-green-700">
                {{ sessionProgressLabel }}
              </p>
            </div>
            <div class="rounded-full bg-white px-3 py-1 text-sm font-semibold text-green-900 shadow-sm">
              {{ sessionStats.correct }} {{ t('duonamo.correct') }}
            </div>
          </div>

          <div
            class="grid gap-1.5"
            :style="{ gridTemplateColumns: `repeat(${sessionChallengeTotal}, minmax(0, 1fr))` }"
          >
            <div
              v-for="step in sessionChallengeTotal"
              :key="step"
              class="h-3 rounded-full border transition-all duration-300"
              :class="getSessionStepClass(step)"
              :title="`Challenge ${step}`"
            />
          </div>
        </div>
      </Card>

      <!-- Current Challenge -->
      <ChallengeComponent
        :key="currentChallenge.id || Math.random()"
        :challenge="currentChallenge"
        @answer="handleAnswer"
      />
    </div>

    <!-- Motivational Modal -->
    <div
      v-if="showMotivationalModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <Card class="max-w-md w-full text-center">
        <div class="space-y-4">
          <div class="text-4xl">🎉</div>
          <h3 class="text-xl font-bold text-primary">
            {{ t('duonamo.sessionComplete') }}
          </h3>

          <div class="grid grid-cols-3 gap-4 py-4 border-y border-gray-200">
            <div>
              <div class="text-lg font-bold text-green-600">
                {{ sessionStats.correct }}
              </div>
              <div class="text-xs text-gray-600">
                {{ t('duonamo.correct') }}
              </div>
            </div>
            <div>
              <div class="text-lg font-bold text-blue-600">{{ accuracy }}%</div>
              <div class="text-xs text-gray-600">
                {{ t('duonamo.accuracy') }}
              </div>
            </div>
            <div>
              <div class="text-lg font-bold text-purple-600">
                {{ sessionStats.xpEarned }}
              </div>
              <div class="text-xs text-gray-600">
                {{ t('duonamo.xpEarned') }}
              </div>
            </div>
          </div>

          <div v-if="currentQuote" class="space-y-2">
            <blockquote class="text-primary italic">"{{ currentQuote.quote_text }}"</blockquote>
            <cite v-if="currentQuote.author" class="text-sm text-gray-600">
              — {{ currentQuote.author }}
            </cite>
          </div>

          <Button variant="primary" class="w-full" @click="closeMotivationalModal">
            {{ t('duonamo.continue') }}
          </Button>
        </div>
      </Card>
    </div>

    <!-- Stats Modal -->
    <StatsModal v-if="showStats" :stats="userStats" @close="showStats = false" />

    <!-- Leaderboard Modal -->
    <LeaderboardCelebration v-if="showLeaderboard" @close="showLeaderboard = false" />
  </LayoutMain>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../stores/auth'
import { useDuoNamoStore } from '../stores/duonamo'
import {
  Brain,
  Play,
  Loader2,
  BarChart3,
  Trophy,
  Target,
  Users,
  Star,
  Award,
  TrendingUp
} from 'lucide-vue-next'
import LayoutMain from '../components/ui/LayoutMain.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import Card from '../components/ui/Card.vue'
import Button from '../components/ui/Button.vue'
import ChallengeComponent from '../components/duonamo/ChallengeComponent.vue'
import StatsModal from '../components/duonamo/StatsModal.vue'
import LeaderboardCelebration from '../components/duonamo/LeaderboardCelebration.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const authStore = useAuthStore()
const duoNamoStore = useDuoNamoStore()
const { t } = useI18n()

// Local state
const loading = ref(false)
const showStats = ref(false)
const showLeaderboard = ref(false)
const userStats = ref(null)
const answerStartTime = ref(null)

// Computed from store (using storeToRefs to preserve reactivity)
const {
  gameProgress,
  currentSession,
  currentChallenge,
  allPeople,
  sessionStats,
  isGameActive,
  showMotivationalModal,
  currentQuote,
  currentLevel,
  totalXP,
  accuracy
} = storeToRefs(duoNamoStore)

const peopleProgressTotal = computed(
  () => userStats.value?.peopleProgress?.total || allPeople.value?.length || 0
)
const peopleProgressDenominator = computed(() => Math.max(peopleProgressTotal.value, 1))
const encounteredPeople = computed(() => {
  const progress = userStats.value?.peopleProgress
  if (!progress) return 0
  return progress.mastered + progress.practiced + progress.learning
})
const notStartedPeople = computed(() => Math.max(peopleProgressTotal.value - encounteredPeople.value, 0))
const encounteredPercent = computed(() =>
  Math.round((encounteredPeople.value / peopleProgressDenominator.value) * 100)
)
const sessionChallengeTotal = computed(() => currentSession.value?.total_challenges || 1)
const currentChallengeNumber = computed(() =>
  Math.min(sessionStats.value.total + 1, sessionChallengeTotal.value)
)
const sessionProgressLabel = computed(
  () => `Spark ${currentChallengeNumber.value} of ${sessionChallengeTotal.value}`
)

function getSessionStepAnswer(step) {
  return sessionStats.value.answers?.[step - 1]
}

function getSessionStepClass(step) {
  const answer = getSessionStepAnswer(step)

  if (answer === true) {
    return 'border-green-500 bg-green-500 shadow-sm'
  }
  if (answer === false) {
    return 'border-rose-400 bg-rose-300 shadow-sm'
  }
  if (step === currentChallengeNumber.value) {
    return 'h-4 -my-0.5 border-green-700 bg-white ring-2 ring-green-200 shadow-sm'
  }
  return 'border-green-200 bg-white/80 opacity-80'
}

onMounted(async () => {
  await authStore.checkAuth()

  if (!authStore.user) {
    router.push('/login')
    return
  }

  try {
    loading.value = true
    await duoNamoStore.initializeGameProgress(authStore.user.id)
    userStats.value = await duoNamoStore.getUserStats()
  } catch (error) {
    console.error('Error initializing DuoNamo:', error)
  } finally {
    loading.value = false
  }
})

// Watch for challenge changes to start timing
watch(currentChallenge, newChallenge => {
  if (newChallenge) {
    answerStartTime.value = Date.now()
  }
})

// Watch for stats modal to refresh stats
watch(showStats, async show => {
  if (show) {
    console.log('Stats modal opened, refreshing stats...')
    userStats.value = await duoNamoStore.getUserStats()
    console.log('Updated stats:', userStats.value)
  }
})

async function startGame() {
  try {
    loading.value = true
    await duoNamoStore.startNewSession()
  } catch (error) {
    console.error('Error starting game:', error)
    alert(t('duonamo.failedToStart'))
  } finally {
    loading.value = false
  }
}

async function handleAnswer(userAnswer) {
  if (!answerStartTime.value) return

  const responseTime = Date.now() - answerStartTime.value

  try {
    const result = await duoNamoStore.submitAnswer(userAnswer, responseTime)

    // Refresh stats if session completed
    if (!isGameActive.value) {
      userStats.value = await duoNamoStore.getUserStats()
    }

    return result
  } catch (error) {
    console.error('Error submitting answer:', error)
  }
}

async function closeMotivationalModal() {
  duoNamoStore.showMotivationalModal = false
  userStats.value = await duoNamoStore.getUserStats()
  gameProgress.value = duoNamoStore.gameProgress
}

function getStartButtonText() {
  if (loading.value) return t('duonamo.loading')
  if (!allPeople.value || allPeople.value.length < 3) return t('duonamo.needMorePeople')
  if (!gameProgress.value?.max_learned) return t('duonamo.startLearningNames')
  return t('duonamo.continueLearning')
}

function getSessionTypeDisplay(type) {
  const types = {
    learning_new: t('duonamo.learningNewNames'),
    mixed_learning: t('duonamo.mixedLearningSession'),
    review_session: t('duonamo.reviewSession'),
    maintenance_review: t('duonamo.maintenanceReview')
  }
  return types[type] || t('duonamo.nameLearning')
}

function formatLastPlayed(dateString) {
  if (!dateString) return t('duonamo.never')

  const date = new Date(dateString)
  const now = new Date()
  const diffHours = Math.floor((now - date) / (1000 * 60 * 60))

  if (diffHours < 1) return t('duonamo.justNow')
  if (diffHours < 24) return `${diffHours}${t('duonamo.hoursAgo')}`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}${t('duonamo.daysAgo')}`

  return date.toLocaleDateString()
}
</script>

<style scoped>
/* Custom animations for game elements */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseSuccess {
  0%,
  100% {
    background-color: rgb(34, 197, 94);
  }
  50% {
    background-color: rgb(22, 163, 74);
  }
}

.slide-in {
  animation: slideInUp 0.3s ease-out;
}

.pulse-success {
  animation: pulseSuccess 0.5s ease-in-out;
}

/* Level progress bar styling */
.level-progress {
  background: linear-gradient(90deg, #3b82f6, #6366f1);
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Card hover effects */
.game-card {
  transition: all 0.2s ease;
}

.game-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}
</style>
